import { Injectable, inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { filter, interval, Observable, switchMap, take, throwError } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { NzModalService } from 'ng-zorro-antd/modal';

import { Logger } from '../services/logger/logger';
import { LOGGER } from '../consts/log.enum';
import { MESSAGE_ERROR_TOKEN_EXPIRED, MESSAGE_ERROR_TOKEN_REFRESH } from '../consts/messages.const';
import { LocalStorageService } from 'ngx-webstorage';
import { INTERCEPTOR_SKIP_ALL_HEADER } from '../consts/interceptor';
import { ProfileService } from '../services/app/profile.service';
import {
  AUTH_TOKEN_HEADER_KEY,
  AUTH_TOKEN_PREFIX,
  AUTH_TOKEN_SKIP,
  AUTH_TOKEN_SKIP_REFRESH,
} from '../consts/http.const';
import { IAuthLogin } from '../types/auth.type';

const REFRESHING_TTL_SECONDS = 15;
const REFRESHING_LOCKING_KEY = 'refreshingAccessTokenLockedBefore';

@Injectable()
export class HttpAuthInterceptor implements HttpInterceptor {
  private logger = inject(Logger);
  private storage = inject(LocalStorageService);
  private profileService = inject(ProfileService);
  private modal = inject(NzModalService);

  private modalShowed = false;

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.headers.has(INTERCEPTOR_SKIP_ALL_HEADER)) {
      return next.handle(request);
    }

    let newRequest: HttpRequest<unknown> = request;

    // add token auth
    const accessToken = this.profileService.accessToken;
    if (!newRequest.headers.has(AUTH_TOKEN_SKIP) && accessToken) {
      if (this.profileService.isTokenExpired() && !newRequest.headers.has(AUTH_TOKEN_SKIP_REFRESH)) {
        return this.handle401Error(newRequest, next);
      }

      newRequest = this.addTokenHeader(newRequest, accessToken);
    }

    return next.handle(newRequest).pipe(
      catchError((res) => {
        // для 401
        if (
          res instanceof HttpErrorResponse &&
          res.status === 401 &&
          !newRequest.headers.has(AUTH_TOKEN_SKIP) &&
          !newRequest.headers.has(AUTH_TOKEN_SKIP_REFRESH)
        ) {
          return this.handle401Error(newRequest, next);
        }

        return throwError(() => res);
      }),
    );
  }

  /**
   * Обработка истекшего токена
   * @param request
   * @param next
   * @private
   */
  private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const refreshToken = this.profileService.refreshToken;
    if (refreshToken && !this.isRefreshingLocked()) {
      this.lockRefreshing();
      this.logger.log(`%cRefresh token`, LOGGER.request);

      const refreshRequest = new HttpRequest('POST', '/auth/session/refresh', { refreshToken });

      return next.handle(refreshRequest).pipe(
        filter((event: HttpEvent<unknown>): event is HttpResponse<IAuthLogin> => event instanceof HttpResponse),
        map((response) => response.body as IAuthLogin),
        finalize(() => {
          this.unlockRefreshing();
        }),
        switchMap((token: IAuthLogin) => {
          this.profileService.setToken(token);
          return next.handle(this.addTokenHeader(request, token.accessToken));
        }),
        catchError((res) => {
          // для повторной ошибки 401
          if (res instanceof HttpErrorResponse && res.status === 401) {
            this.logger.error(`Refresh token failed`, res);

            this.profileService.logout();
            this.modalShowed = true;
            this.modal.error({
              nzTitle: MESSAGE_ERROR_TOKEN_EXPIRED,
              nzClosable: false,
              nzOnOk: () => {
                this.modalShowed = false;
                window.location.reload();
              },
            });
          } else {
            this.errorRefreshToken();
          }

          return throwError(() => res);
        }),
      );
    }

    // Каждые 100 мс проверяем токен доступа, т.к. он может обновиться как из параллельно запущенного запроса,
    // так и из соседней вкладки.
    // Если в течении установленного лимита не был получен свежий токен, то выполняем запрос со старым токеном
    // (скорее всего он выполнится неудачно, что приведёт к разлогиниванию)
    return interval(100).pipe(
      map(() => {
        // выходим из цикла если появился актуальный токен или истекло время на обновление токена
        return !(this.profileService.isTokenExpired() && this.isRefreshingLocked());
      }),
      filter((isContinue) => isContinue),
      take(1),
      switchMap(() => {
        const accessToken = this.profileService.accessToken;
        if (this.profileService.isTokenExpired() || !accessToken) {
          this.errorRefreshToken();
          return throwError(() => ({ error: { message: 'Refresh token failed' } }));
        } else {
          return next.handle(this.addTokenHeader(request, accessToken));
        }
      }),
    );
  }

  /**
   * Добавляем токен авторизации
   */
  private addTokenHeader(request: HttpRequest<unknown>, token: string) {
    return request.clone({
      headers: request.headers.set(AUTH_TOKEN_HEADER_KEY, AUTH_TOKEN_PREFIX + token),
    });
  }

  private isRefreshingLocked(): boolean {
    let lockedBefore = this.storage.retrieve(REFRESHING_LOCKING_KEY);
    if (lockedBefore == null) {
      return false;
    }
    lockedBefore = new Date(lockedBefore);
    return lockedBefore > new Date();
  }

  private lockRefreshing() {
    const lockedBefore = new Date(new Date().getTime() + REFRESHING_TTL_SECONDS * 1000);
    this.storage.store(REFRESHING_LOCKING_KEY, lockedBefore);
  }

  private unlockRefreshing() {
    this.storage.store(REFRESHING_LOCKING_KEY, null);
  }

  private errorRefreshToken() {
    if (!this.modalShowed) {
      this.modalShowed = true;
      this.logger.error(`Refresh token failed`);

      this.modal.confirm({
        nzTitle: MESSAGE_ERROR_TOKEN_REFRESH,
        nzIconType: 'close-circle',
        nzOkText: 'Обновить',
        nzOnOk: () => {
          this.modalShowed = false;
          window.location.reload();
        },
        nzCancelText: 'Отмена',
        nzOnCancel: () => {
          this.modalShowed = false;
        },
      });
    }
  }
}
